import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import axios from 'axios';

import { StatusFilter } from 'app/shared/model/enumerations/status-filter.model';
import { IUserCustomAdmin } from 'app/shared/model/user-custom-admin.model';
import { defaultValue, IUser } from 'app/shared/model/user.model';
import { IQueryParams, serializeAxiosError } from 'app/shared/reducers/reducer.utils';

const initialState = {
  loading: false,
  errorMessage: null,
  users: [] as ReadonlyArray<IUser>,
  usersCustomAdmin: [] as ReadonlyArray<IUserCustomAdmin>,
  authorities: [] as any[],
  user: defaultValue,
  userCustomAdmin: {} as any,
  updating: false,
  updateSuccess: false,
  totalItems: 0,
};

const apiUrl = 'api/users';
const adminUrl = 'api/admin/users';
const customAdminUrl = 'api/admin/users-all-custom';
const getUserCustom = 'api/admin/users-custom';
const updateUserCustom = 'api/admin/users-update-custom';

// Async Actions

export const getUsers = createAsyncThunk('userManagement/fetch_users', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}` : ''}`;
  return axios.get<IUser[]>(requestUrl);
});

interface IUserListQueryParams extends IQueryParams {
  emailSearch?: string;
  companySearch?: string;
  nameSearch?: string;
  identificationCodeSearch?: string;
  status?: StatusFilter | string;
}
export const getUsersAsAdmin = createAsyncThunk(
  'userManagement/fetch_users_as_admin',
  async ({ page, size, sort, emailSearch, companySearch, nameSearch, identificationCodeSearch, status }: IUserListQueryParams) => {
    const onEmailSearch = `&emailSearch=${encodeURIComponent(emailSearch?.toUpperCase()) ?? ''}`;
    const onCompanySearch = `&companySearch=${encodeURIComponent(companySearch?.toUpperCase()) ?? ''}`;
    const onNameSearch = `&nameSearch=${encodeURIComponent(nameSearch?.toUpperCase()) ?? ''}`;
    const onIdentificationCodeSearch = `&identificationCodeSearch=${encodeURIComponent(identificationCodeSearch?.toUpperCase()) ?? ''}`;

    const requestUrl = `${customAdminUrl}${`?page=${page}&size=${size}&sorted=${sort}${onEmailSearch}${onCompanySearch}${onNameSearch}${onIdentificationCodeSearch}&status=${status}&cacheBuster=${new Date().getTime()}`}`;
    return axios.get<IUserCustomAdmin[]>(requestUrl);
  }
);

export const getRoles = createAsyncThunk('userManagement/fetch_roles', async () => {
  return axios.get<any[]>(`api/authorities`);
});

export const getUser = createAsyncThunk(
  'userManagement/fetch_user',
  async (id: string) => {
    const requestUrl = `${adminUrl}/${id}`;
    return axios.get<IUser>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getUserCustomAdmin = createAsyncThunk(
  'userManagement/fetch_user_custom_admin',
  async (id: string) => {
    const requestUrl = `${getUserCustom}/${id}`;
    return axios.get<IUserCustomAdmin>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createUser = createAsyncThunk(
  'userManagement/create_user',
  async (user: IUser, thunkAPI) => {
    const result = await axios.post<IUser>(adminUrl, user);
    thunkAPI.dispatch(getUsersAsAdmin({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateUser = createAsyncThunk(
  'userManagement/update_user',
  async (user: IUser) => {
    const result = await axios.put<IUser>(adminUrl, user);
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateUserCustomAdmin = createAsyncThunk(
  'userManagement/update_user',
  async (userCustom: IUser, thunkAPI) => {
    const result = await axios.put<IUser>(updateUserCustom, userCustom);
    thunkAPI.dispatch(getUsersAsAdmin({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteUser = createAsyncThunk(
  'userManagement/delete_user',
  async (id: string, thunkAPI) => {
    const requestUrl = `${adminUrl}/${id}`;
    const result = await axios.delete<IUser>(requestUrl);
    thunkAPI.dispatch(getUsersAsAdmin({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export type UserManagementState = Readonly<typeof initialState>;

export const UserManagementSlice = createSlice({
  name: 'userManagement',
  initialState: initialState as UserManagementState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getRoles.fulfilled, (state, action) => {
        state.authorities = action.payload.data;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.data;
      })
      .addCase(getUserCustomAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.userCustomAdmin = action.payload.data;
      })
      .addCase(deleteUser.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.user = defaultValue;
      })
      .addMatcher(isFulfilled(getUsers, getUsersAsAdmin), (state, action) => {
        state.loading = false;
        state.users = action.payload.data;
        state.usersCustomAdmin = action.payload.data;
        state.totalItems = parseInt(action.payload.headers['x-total-count'], 10);
      })
      .addMatcher(isFulfilled(createUser, updateUser, updateUserCustomAdmin), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.user = action.payload.data;
      })
      .addMatcher(isPending(getUsers, getUsersAsAdmin, getUser), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createUser, updateUser, deleteUser, updateUserCustomAdmin), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isRejected(getUsers, getUsersAsAdmin, getUser, getRoles, createUser, updateUser, deleteUser, updateUserCustomAdmin), (state, action) => {
        state.loading = false;
        state.updating = false;
        state.updateSuccess = false;
        state.errorMessage = action.error.message;
      });
  },
});

export const { reset } = UserManagementSlice.actions;

// Reducer
export default UserManagementSlice.reducer;
